import { useEffect, useRef, useState, type ReactNode } from 'react'
import { create } from 'zustand'
import { cn } from '@/lib/utils'
import { BottomSheet } from './BottomSheet'

/**
 * ConfirmDialog — the app's replacement for `window.confirm()` (UX-138).
 *
 * Composes <BottomSheet> (a sheet on phones, a centered card on desktop), so
 * it gets focus trapping, Esc, the sheet stack and an accessible name.
 *
 * Copy rules (docs/ux/VOICE.md):
 *   - Title asks the question: "Delete this quote?"
 *   - `impact` says what happens and what stays: "QT-2026-002 ($7,516.75)
 *     will be removed. Brad stays in Contacts."
 *   - The confirm button is a verb + object: "Delete quote", "Rotate link".
 *     Never "OK" / "Yes" / "Confirm".
 *   - The cancel button says what stays: "Keep it" (default), never a bare
 *     "Cancel" (ambiguous next to "Cancel this visit").
 *   - Multiple outcomes → `choices`: "Cancel this visit only" /
 *     "Cancel all future visits" / "Keep visits".
 */

export type ConfirmTone = 'danger' | 'primary'

export interface ConfirmChoice<V extends string = string> {
  value: V
  label: string
  tone?: ConfirmTone
}

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  /** Single-outcome confirm. Not used when `choices` is given. */
  onConfirm?: () => void
  title: string
  /** What will happen / what stays. String or rich content. */
  message?: ReactNode
  /** Alias of `message` (reads better at call sites). */
  impact?: ReactNode
  /** Verb + object: "Delete quote". Default "Continue" — always pass one. */
  confirmLabel?: string
  /** What stays: default "Keep it". */
  cancelLabel?: string
  variant?: ConfirmTone
  /** Shows "Working…" and blocks dismissal while the action runs. */
  isLoading?: boolean
  /** Several outcomes, each its own button (top = first). */
  choices?: ConfirmChoice[]
  onChoose?: (value: string) => void
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  impact,
  confirmLabel = 'Continue',
  cancelLabel = 'Keep it',
  variant = 'danger',
  isLoading = false,
  choices,
  onChoose,
}: ConfirmDialogProps) {
  const body = impact ?? message
  const btn = (tone: ConfirmTone) =>
    cn('ds-btn ds-btn-lg w-full sm:w-auto', tone === 'danger' ? 'ds-btn-danger' : 'ds-btn-primary')

  const actions: ConfirmChoice[] = choices && choices.length > 0
    ? choices
    : [{ value: '__confirm__', label: confirmLabel, tone: variant }]

  return (
    <BottomSheet
      open={isOpen}
      onClose={onClose}
      title={title}
      // Three buttons (cancel + two choices) need the wider sheet to sit in one row.
      size={actions.length > 1 ? 'md' : 'sm'}
      dismissible={!isLoading}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end" data-testid="confirm-actions">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="ds-btn ds-btn-lg w-full sm:w-auto disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {actions.map((c) => (
            <button
              key={c.value}
              type="button"
              disabled={isLoading}
              className={btn(c.tone ?? variant)}
              onClick={() => {
                if (c.value === '__confirm__') onConfirm?.()
                else onChoose?.(c.value)
              }}
            >
              {isLoading && actions.length === 1 ? 'Working…' : c.label}
            </button>
          ))}
        </div>
      }
    >
      {body ? (
        <div className="text-[15px] leading-relaxed" style={{ color: 'var(--midnight-text2)' }}>
          {body}
        </div>
      ) : null}
    </BottomSheet>
  )
}

// ─── imperative API: `await confirm({...})` ─────────────────────────────────

export interface ConfirmRequest {
  title: string
  impact?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  choices?: ConfirmChoice[]
}

interface PendingConfirm extends ConfirmRequest {
  id: number
  resolve: (value: string | null) => void
}

interface ConfirmStore {
  pending: PendingConfirm | null
  open: (req: ConfirmRequest) => Promise<string | null>
  settle: (value: string | null) => void
}

let confirmSeq = 0

export const useConfirmStore = create<ConfirmStore>((set, get) => ({
  pending: null,
  open: (req) =>
    new Promise<string | null>((resolve) => {
      // A second request replaces an unanswered first one (answered "keep").
      get().pending?.resolve(null)
      set({ pending: { ...req, id: ++confirmSeq, resolve } })
    }),
  settle: (value) => {
    const p = get().pending
    if (!p) return
    set({ pending: null })
    p.resolve(value)
  },
}))

/**
 * Promise-based confirm, a drop-in for `window.confirm()`:
 *
 *   const { confirm, choose } = useConfirm()
 *   if (!(await confirm({ title: 'Delete this service?', impact: '…', confirmLabel: 'Delete service' }))) return
 *
 *   const which = await choose({
 *     title: 'Cancel this visit?',
 *     choices: [
 *       { value: 'one', label: 'Cancel this visit only' },
 *       { value: 'future', label: 'Cancel all future visits' },
 *     ],
 *     cancelLabel: 'Keep visits',
 *   })  // 'one' | 'future' | null
 *
 * Needs <ConfirmHost/> mounted once (App.tsx does this).
 */
export function useConfirm() {
  const open = useConfirmStore((s) => s.open)
  return {
    confirm: async (req: Omit<ConfirmRequest, 'choices'>): Promise<boolean> =>
      (await open(req)) === '__confirm__',
    choose: async <V extends string>(req: ConfirmRequest & { choices: ConfirmChoice<V>[] }): Promise<V | null> =>
      (await open(req)) as V | null,
  }
}

/** Renders the pending imperative confirm. Mount once near the app root. */
export function ConfirmHost() {
  const pending = useConfirmStore((s) => s.pending)
  const settle = useConfirmStore((s) => s.settle)
  // Keep the last request's copy on screen during the exit animation.
  const [shown, setShown] = useState<PendingConfirm | null>(pending)
  const lastId = useRef<number | null>(null)
  useEffect(() => {
    if (pending && pending.id !== lastId.current) {
      lastId.current = pending.id
      setShown(pending)
    }
  }, [pending])

  if (!shown) return null
  return (
    <ConfirmDialog
      isOpen={!!pending}
      onClose={() => settle(null)}
      onConfirm={() => settle('__confirm__')}
      onChoose={(v) => settle(v)}
      title={shown.title}
      impact={shown.impact}
      confirmLabel={shown.confirmLabel}
      cancelLabel={shown.cancelLabel}
      variant={shown.tone ?? 'danger'}
      choices={shown.choices}
    />
  )
}
